// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Power, PowerOff, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useToggleVendorActiveMutation, type VendorRow } from "@/api/hooks/use-vendors";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ToggleVendorActiveDialogProps {
  vendor: VendorRow | null;
  onClose: () => void;
}

export function ToggleVendorActiveDialog({ vendor, onClose }: ToggleVendorActiveDialogProps) {
  const { t } = useTranslation("back-office");
  const lastVendor = useRef(vendor);
  if (vendor) lastVendor.current = vendor;
  const display = vendor ?? lastVendor.current;
  const toggleActive = useToggleVendorActiveMutation(display?.name ?? "");

  async function onSubmit() {
    if (!display) return;
    const newActive = !display.isActive;
    try {
      await toggleActive.mutateAsync(newActive);
      toast.success(
        newActive
          ? t("vendors.notifications.activated", { name: display.name ?? `#${display.vendorId}` })
          : t("vendors.notifications.deactivated", { name: display.name ?? `#${display.vendorId}` }),
      );
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!vendor} onOpenChange={() => onClose()}>
      <DialogContent
        id="dialog-toggle-vendor-status"
        className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col"
      >
        <div className="flex flex-row">
          <WizardPane
            variant={display?.isActive ? "destructive" : "default"}
            icon={display?.isActive ? PowerOff : Power}
          />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>
                {display?.isActive ? t("vendors.dialog.deactivateTitle") : t("vendors.dialog.activateTitle")}
              </DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p>
                    {display?.isActive ? t("vendors.dialog.deactivatePrompt") : t("vendors.dialog.activatePrompt")}{" "}
                    <span className="font-semibold text-foreground">{display?.name ?? `#${display?.vendorId}`}</span>?
                  </p>
                  {display?.isActive && (
                    <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                      <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                      <span className="text-sm">{t("vendors.dialog.deactivateWarning")}</span>
                    </div>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant={display?.isActive ? "destructive" : "default"}
            className={
              display?.isActive
                ? ""
                : "bg-green-700 text-white hover:bg-green-800 dark:bg-green-700 dark:hover:bg-green-600"
            }
            disabled={toggleActive.isPending}
            onClick={onSubmit}
          >
            {toggleActive.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : display?.isActive ? (
              <PowerOff className="h-4 w-4" />
            ) : (
              <Power className="h-4 w-4" />
            )}
            {display?.isActive ? t("vendors.dialog.deactivateConfirm") : t("vendors.dialog.activateConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
